import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Phone, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

import {
  sLabel,
  haversineKm, normalizeTrackingCode,
  getPrimaryStopCoords,
  urlBase64ToUint8Array, compressImage,
} from "./driver/helpers";
import { AbsenceRequestSection } from "./driver/AbsenceRequestSection";
import { DriverHeader } from "./driver/DriverHeader";
import { HomeScreen } from "./driver/screens/HomeScreen";
import { ListScreen } from "./driver/screens/ListScreen";
import { DetailScreen } from "./driver/screens/DetailScreen";
import { PodScreen } from "./driver/screens/PodScreen";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_6e85a7b3-7844-4782-8600-31defd3a9a43/artifacts/5fhyey8q_MV%20trasporti%20logo.png";

async function subscribeToPush(token) {
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !VAPID_PUBLIC_KEY) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) });
    const j = sub.toJSON();
    if (!j.endpoint || !j.keys?.p256dh) return false;
    await axios.post(`${API}/driver/push-subscribe`, { endpoint: j.endpoint, keys: j.keys }, { headers: { Authorization: `Bearer ${token}` } });
    return true;
  } catch (e) { console.error("Push subscribe:", e); return false; }
}

async function unsubscribeFromPush(token) {
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const j = sub.toJSON();
      await axios.post(`${API}/driver/push-unsubscribe`, { endpoint: j.endpoint, keys: j.keys }, { headers: { Authorization: `Bearer ${token}` } });
      await sub.unsubscribe();
    }
  } catch (e) { console.error("Push unsubscribe:", e); }
}

// ====== LOGIN ======
function LoginScreen({ onLogin }) {
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const handle = async (e) => {
    e.preventDefault();
    if (!phone || !password) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/driver/login`, { email: phone, password });
      localStorage.setItem("mv_driver_token", r.data.token);
      localStorage.setItem("mv_driver_name", r.data.name);
      localStorage.setItem("mv_driver_plate", r.data.plate);
      onLogin(r.data);
    } catch { toast.error("Credenziali non valide"); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="MV" className="h-12 mx-auto mb-4" style={{ filter: "invert(1)" }} />
          <h1 className="text-white text-2xl font-bold uppercase tracking-wider">App Autisti</h1>
          <p className="text-zinc-500 text-sm mt-1">Accedi con il tuo numero di telefono</p>
        </div>
        <form onSubmit={handle} className="space-y-4">
          <div>
            <label htmlFor="drv-phone" className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1.5">Telefono</label>
            <div className="relative">
              <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input id="drv-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full h-12 pl-10 pr-4 bg-zinc-900 border border-zinc-800 text-white rounded-sm focus:border-[#FF5200] outline-none text-sm" placeholder="3331234567" data-testid="driver-login-phone" />
            </div>
          </div>
          <div>
            <label htmlFor="drv-pass" className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1.5">Password</label>
            <input id="drv-pass" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full h-12 px-4 bg-zinc-900 border border-zinc-800 text-white rounded-sm focus:border-[#FF5200] outline-none text-sm" data-testid="driver-login-password" />
          </div>
          <button type="submit" disabled={loading} className="w-full h-12 bg-[#FF5200] text-white font-bold uppercase tracking-wide rounded-sm text-sm disabled:opacity-50 flex items-center justify-center gap-2" data-testid="driver-login-btn">
            {loading ? <Loader2 size={16} className="animate-spin" /> : "Accedi"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ====== MAIN APP ======
export default function DriverApp() {
  const [loggedIn, setLoggedIn] = useState(!!localStorage.getItem("mv_driver_token"));
  const [driverInfo, setDriverInfo] = useState({ name: localStorage.getItem("mv_driver_name") || "", plate: localStorage.getItem("mv_driver_plate") || "" });
  const [work, setWork] = useState({ shipments: [], pickups: [] });
  const [screen, setScreen] = useState("home"); // home, list, detail, pod, absences
  const [selectedJob, setSelectedJob] = useState(null);
  // Multi-POD state
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [multiSelectedIds, setMultiSelectedIds] = useState([]);
  const [multiPodJobs, setMultiPodJobs] = useState([]);
  const [showMultiPodWarning, setShowMultiPodWarning] = useState(false);
  const [pendingMultiPodJobs, setPendingMultiPodJobs] = useState([]);

  const navigateTo = useCallback((newScreen, job) => {
    if (job !== undefined) setSelectedJob(job);
    setScreen(newScreen);
    window.history.pushState({ screen: newScreen }, "");
  }, []);

  const goBack = useCallback(() => {
    if (screen === "pod") {
      if (multiPodJobs.length > 0) { setMultiPodJobs([]); setScreen("home"); }
      else { setScreen("detail"); }
    }
    else if (screen === "detail") { setScreen("home"); setSelectedJob(null); }
    else if (screen === "list") { setScreen("home"); }
    else if (screen === "absences") { setScreen("home"); }
    else { return; }
  }, [screen, multiPodJobs.length]);

  useEffect(() => {
    const handlePopState = () => {
      if (screen === "home") {
        window.history.pushState({ screen: "home" }, "");
        return;
      }
      goBack();
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [screen, goBack]);

  useEffect(() => {
    window.history.replaceState({ screen: "home" }, "");
  }, []);

  const [installPrompt, setInstallPrompt] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [showPushHelp, setShowPushHelp] = useState(false);
  const [myLocation, setMyLocation] = useState(null);
  const [orderVersion, setOrderVersion] = useState(0);

  // POD state
  const [podPhoto, setPodPhoto] = useState("");
  const [podSignature, setPodSignature] = useState("");
  const [podSignedBy, setPodSignedBy] = useState("");
  const [podNote, setPodNote] = useState("");
  const [showSignature, setShowSignature] = useState(false);

  // Anomaly state
  const [showAnomaly, setShowAnomaly] = useState(false);
  const [anomalyType, setAnomalyType] = useState("");
  const [anomalyNote, setAnomalyNote] = useState("");
  const [anomalyPhoto, setAnomalyPhoto] = useState("");

  const [listTab, setListTab] = useState("active");
  const [saving, setSaving] = useState(false);

  // Timesheet state
  const [shiftStatus, setShiftStatus] = useState("off");
  const [shiftStartTime, setShiftStartTime] = useState(null);
  const [breakCount, setBreakCount] = useState(0);
  const [confirmEvent, setConfirmEvent] = useState(null);
  const [showEndShiftSlide, setShowEndShiftSlide] = useState(false);

  const resetPodForm = useCallback((job = null) => {
    setPodPhoto(job?.pod?.photo || "");
    setPodSignature(job?.pod?.signature || "");
    setPodSignedBy(job?.pod?.signed_by || "");
    setPodNote(job?.pod?.note || "");
    setShowSignature(false);
  }, []);

  const token = localStorage.getItem("mv_driver_token");
  const headers = { headers: { Authorization: `Bearer ${token}` } };

  // PWA & SW
  useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    let link = document.querySelector('link[rel="manifest"][data-driver]');
    if (!link) { link = document.createElement("link"); link.rel = "manifest"; link.href = "/manifest-driver.json"; link.setAttribute("data-driver", "true"); document.head.appendChild(link); }
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", "#09090B");
    if ("serviceWorker" in navigator) {
      if (isIOS) {
        navigator.serviceWorker.getRegistrations().then((regs) => Promise.all(regs.map((reg) => reg.unregister()))).catch(() => {});
      } else {
        navigator.serviceWorker.register("/sw-driver.js").catch(() => {});
      }
    }
    const h = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener("beforeinstallprompt", h);
    return () => { window.removeEventListener("beforeinstallprompt", h); if (link) link.remove(); };
  }, []);

  // Geolocation - REQUIRED
  const [gpsBlocked, setGpsBlocked] = useState(false);
  useEffect(() => {
    if (!loggedIn) return;
    if (!navigator.geolocation) { setGpsBlocked(true); return; }
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const acc = pos.coords.accuracy;
        if (acc && acc > 500) return;
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setMyLocation(loc);
        setGpsBlocked(false);
        if (token) axios.post(`${API}/driver/update-location`, loc, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
      },
      (err) => { if (err.code === 1) setGpsBlocked(true); },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 30000 }
    );
    return () => { if (watchId) navigator.geolocation.clearWatch(watchId); };
  }, [loggedIn, token]);

  // Push check
  useEffect(() => {
    if (!loggedIn) return;
    (async () => {
      if ("PushManager" in window) {
        try { const reg = await navigator.serviceWorker.ready; setPushEnabled(!!(await reg.pushManager.getSubscription())); } catch { /* ignore */ }
      }
    })();
  }, [loggedIn]);

  const logout = useCallback(() => { localStorage.removeItem("mv_driver_token"); localStorage.removeItem("mv_driver_name"); localStorage.removeItem("mv_driver_plate"); setLoggedIn(false); }, []);

  const loadWork = useCallback(() => {
    if (!token) return;
    axios.get(`${API}/driver/my-work`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setWork(r.data))
      .catch((err) => { if (err.response?.status === 401) logout(); });
  }, [token, logout]);

  useEffect(() => {
    if (!loggedIn) return;
    loadWork();
    const iv = setInterval(loadWork, 5 * 60 * 1000);
    return () => clearInterval(iv);
  }, [loggedIn, loadWork]);

  // Timesheet
  const loadTimesheet = useCallback(() => {
    if (!token) return;
    axios.get(`${API}/driver/timesheet/today`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const events = r.data?.events || [];
        let status = "off";
        let startTime = null;
        let breaks = 0;
        for (const ev of events) {
          if (ev.type === "start_shift") { status = "working"; startTime = ev.time; }
          else if (ev.type === "end_shift") { status = "off"; startTime = null; }
          else if (ev.type === "start_break") { status = "break"; breaks++; }
          else if (ev.type === "end_break") { status = "working"; }
        }
        setShiftStatus(status);
        setShiftStartTime(startTime);
        setBreakCount(breaks);
      })
      .catch(() => {});
  }, [token]);

  useEffect(() => { if (loggedIn) loadTimesheet(); }, [loggedIn, loadTimesheet]);

  const recordTimesheetEvent = async (type) => {
    try {
      await axios.post(`${API}/driver/timesheet/event`, {
        type,
        lat: myLocation?.lat || null,
        lng: myLocation?.lng || null,
      }, headers);
      const labels = { start_shift: "Turno iniziato", end_shift: "Turno terminato", start_break: "Pausa iniziata", end_break: "Pausa terminata" };
      toast.success(labels[type] || type);
      loadTimesheet();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore registrazione");
    }
  };

  const requestTimesheetEvent = (type) => {
    if (type === "end_shift") { setShowEndShiftSlide(true); return; }
    const meta = {
      start_shift: { label: "Inizio Turno", color: "bg-green-600", desc: "Stai per registrare l'inizio del tuo turno di lavoro." },
      start_break: { label: "Inizio Pausa", color: "bg-amber-500", desc: "Stai per iniziare una pausa. Il tempo verra scalato dalle ore lavorate." },
      end_break: { label: "Fine Pausa", color: "bg-green-600", desc: "Stai per terminare la pausa e tornare al lavoro." },
    };
    setConfirmEvent({ type, ...meta[type] });
  };

  const togglePush = async () => {
    if (!("Notification" in window) || !("PushManager" in window)) { toast.error("Notifiche push non supportate."); return; }
    if (pushEnabled) { await unsubscribeFromPush(token); setPushEnabled(false); toast.success("Notifiche disattivate"); }
    else {
      if (Notification.permission === "denied") { setShowPushHelp(true); return; }
      const perm = await Notification.requestPermission();
      if (perm === "granted") { const ok = await subscribeToPush(token); if (ok) { setPushEnabled(true); toast.success("Notifiche attivate!"); } else toast.error("Errore attivazione"); }
      else if (perm === "denied") setShowPushHelp(true);
    }
  };

  const allItems = useMemo(() => [...work.shipments.map((s) => ({ ...s, _type: "shipment" })), ...work.pickups.map((p) => ({ ...p, _type: "pickup" }))], [work.shipments, work.pickups]);
  const active = allItems.filter((i) => i.status !== "consegnato");
  const completed = allItems.filter((i) => i.status === "consegnato");
  const savedOrder = (() => { try { return JSON.parse(localStorage.getItem("mv_driver_order") || "[]"); } catch { return []; } })();

  const handleScan = (text) => {
    const normalizedText = normalizeTrackingCode(text);
    setShowScanner(false);
    const item = allItems.find((i) => i.tracking_number?.toUpperCase() === normalizedText);
    if (item) { navigateTo("detail", item); toast.success(`Trovato: ${normalizedText}`); }
    else toast.error(`${normalizedText} non trovato nei tuoi lavori`);
  };

  const distanceTo = (job) => {
    if (!myLocation) return null;
    const coords = getPrimaryStopCoords(job);
    if (coords && coords.length === 2) return haversineKm(myLocation.lat, myLocation.lng, coords[0], coords[1]);
    return null;
  };

  const [routeEtas, setRouteEtas] = useState({});
  const lastEtaFetchRef = useRef({ key: "", t: 0 });

  useEffect(() => {
    if (!myLocation || !token || active.length === 0) return;
    const lat = Math.round(myLocation.lat * 100) / 100;
    const lng = Math.round(myLocation.lng * 100) / 100;
    const ids = active.map((j) => j.id).sort().join(",");
    const key = `${lat},${lng}|${ids}`;
    const now = Date.now();
    if (lastEtaFetchRef.current.key === key && now - lastEtaFetchRef.current.t < 2 * 60 * 1000) return;
    lastEtaFetchRef.current = { key, t: now };
    const jobs = active
      .map((j) => {
        const c = getPrimaryStopCoords(j);
        return c ? { id: j.id, lat: c[0], lng: c[1] } : null;
      })
      .filter(Boolean);
    if (jobs.length === 0) return;
    axios
      .post(`${API}/driver/route-eta`, { from: { lat: myLocation.lat, lng: myLocation.lng }, jobs }, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setRouteEtas((prev) => ({ ...prev, ...(r.data?.etas || {}) })))
      .catch(() => {});
  }, [myLocation, token, active]);

  const etaBadge = (job) => {
    const osrm = routeEtas[job.id];
    if (osrm && osrm.minutes != null) return `${osrm.minutes} min \u00b7 ${osrm.km} km`;
    const d = distanceTo(job);
    if (d === null) return null;
    return d < 1 ? `${Math.round(d * 1000)} m` : `${d.toFixed(1)} km`;
  };

  const sortedActive = useMemo(() => {
    return [...active].sort((a, b) => {
      const ai = savedOrder.indexOf(a.id);
      const bi = savedOrder.indexOf(b.id);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      const ao = a.order_index ?? 9999;
      const bo = b.order_index ?? 9999;
      if (ao !== bo) return ao - bo;
      return new Date(a.created_at || 0) - new Date(b.created_at || 0);
    });
  }, [active, savedOrder]);

  const nextJob = sortedActive.length > 0 ? sortedActive[0] : null;
  const nextDist = nextJob ? distanceTo(nextJob) : null;
  const nextOsrm = nextJob ? routeEtas[nextJob.id] : null;
  const nextEta = nextOsrm?.minutes ?? (nextDist ? Math.max(5, Math.round((nextDist * 1.3) / 40 * 60)) : null);

  const prevScreenRef = useRef(screen);
  useEffect(() => {
    const enteringPod = screen === "pod" && prevScreenRef.current !== "pod";
    prevScreenRef.current = screen;
    if (enteringPod && selectedJob) {
      const currentJob = allItems.find((i) => i.id === selectedJob.id) || selectedJob;
      resetPodForm(currentJob);
    }
  }, [screen, selectedJob, allItems, resetPodForm]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    window.__mvDriverTestScan = handleScan;
    return () => { delete window.__mvDriverTestScan; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allItems]);

  // Update job status
  const updateStatus = async (job, status) => {
    setSaving(true);
    const prefix = job._type === "shipment" ? "shipments" : "pickups";
    try {
      await axios.post(`${API}/driver/${prefix}/${job.id}/status`, { status, location: "", note: "" }, headers);
      toast.success(`Stato aggiornato: ${sLabel(status)}`);
      loadWork();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  // Submit POD (supports both single-job and multi-job)
  const submitPod = async () => {
    if (!podSignedBy) { toast.error("Inserisci il nome di chi firma"); return; }
    if (!podSignature) { toast.error("Firma richiesta"); return; }

    if (multiPodJobs.length > 0) {
      setSaving(true);
      try {
        const geolocation = myLocation ? { lat: myLocation.lat, lng: myLocation.lng, address: "" } : {};
        const results = await Promise.allSettled(multiPodJobs.map((job) => {
          const prefix = job._type === "shipment" ? "shipments" : "pickups";
          return axios.post(`${API}/driver/${prefix}/${job.id}/pod`, {
            photo: podPhoto, signature: podSignature, signed_by: podSignedBy, note: podNote, geolocation,
          }, headers);
        }));
        const ok = results.filter((r) => r.status === "fulfilled").length;
        const fail = results.length - ok;
        if (fail === 0) toast.success(`${ok} consegne confermate!`);
        else if (ok > 0) toast.warning(`${ok} confermate, ${fail} in errore`);
        else toast.error("Errore invio POD");
        setMultiPodJobs([]);
        setMultiSelectedIds([]);
        setMultiSelectMode(false);
        resetPodForm();
        navigateTo("home");
        setSelectedJob(null);
        loadWork();
      } finally { setSaving(false); }
      return;
    }

    setSaving(true);
    const prefix = selectedJob._type === "shipment" ? "shipments" : "pickups";
    try {
      await axios.post(`${API}/driver/${prefix}/${selectedJob.id}/pod`, {
        photo: podPhoto, signature: podSignature, signed_by: podSignedBy, note: podNote,
        geolocation: myLocation ? { lat: myLocation.lat, lng: myLocation.lng, address: "" } : {}
      }, headers);
      toast.success(selectedJob?.pod ? "Consegna aggiornata!" : "Consegna confermata!");
      resetPodForm();
      navigateTo("home");
      setSelectedJob(null);
      loadWork();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const submitAnomaly = async () => {
    if (!anomalyType) { toast.error("Seleziona tipo anomalia"); return; }
    setSaving(true);
    const prefix = selectedJob._type === "shipment" ? "shipments" : "pickups";
    try {
      await axios.post(`${API}/driver/${prefix}/${selectedJob.id}/anomaly`, {
        type: anomalyType, note: anomalyNote, photo: anomalyPhoto,
        geolocation: myLocation ? { lat: myLocation.lat, lng: myLocation.lng } : {}
      }, headers);
      toast.success("Anomalia segnalata!");
      setShowAnomaly(false); setAnomalyType(""); setAnomalyNote(""); setAnomalyPhoto("");
      loadWork();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  // Camera
  const [preferredCamera, setPreferredCamera] = useState(() => localStorage.getItem("mv_driver_camera") || "environment");

  const capturePhoto = async (setter) => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "image/*"; input.capture = preferredCamera;
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) { const compressed = await compressImage(file); setter(compressed); toast.success("Foto acquisita"); }
    };
    input.click();
  };

  const toggleCamera = () => {
    const next = preferredCamera === "environment" ? "user" : "environment";
    setPreferredCamera(next);
    localStorage.setItem("mv_driver_camera", next);
    toast.info(next === "environment" ? "Fotocamera posteriore" : "Fotocamera anteriore");
  };

  const handleReorder = (from, to) => {
    const ids = sortedActive.map((i) => i.id);
    const [moved] = ids.splice(from, 1);
    ids.splice(to, 0, moved);
    localStorage.setItem("mv_driver_order", JSON.stringify(ids));
    setOrderVersion((v) => v + 1);
  };

  // Multi-POD helpers
  const toggleMultiSelect = (jobId) => {
    setMultiSelectedIds((prev) => prev.includes(jobId) ? prev.filter((x) => x !== jobId) : [...prev, jobId]);
  };
  const exitMultiSelect = () => { setMultiSelectMode(false); setMultiSelectedIds([]); };
  const proceedWithMultiPod = (jobs) => {
    setMultiPodJobs(jobs);
    resetPodForm();
    navigateTo("pod");
  };
  const startMultiPod = () => {
    const jobs = multiSelectedIds
      .map((id) => allItems.find((i) => i.id === id))
      .filter((j) => j && j.status !== "consegnato");
    if (jobs.length === 0) { toast.error("Nessuna consegna valida selezionata"); return; }
    const uniqueClients = new Set(jobs.map((j) => (j.client_email || "__nessuno__").toLowerCase()));
    if (uniqueClients.size > 1) {
      setPendingMultiPodJobs(jobs);
      setShowMultiPodWarning(true);
      return;
    }
    proceedWithMultiPod(jobs);
  };

  if (!loggedIn) return <LoginScreen onLogin={(d) => { setDriverInfo(d); setLoggedIn(true); }} />;

  // GPS REQUIRED OVERLAY
  if (gpsBlocked) {
    return (
      <div className="min-h-screen bg-[#09090B] text-white flex items-center justify-center p-6" data-testid="gps-required-overlay">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <MapPin size={32} className="text-red-500" />
          </div>
          <h2 className="text-lg font-bold mb-2">GPS Obbligatorio</h2>
          <p className="text-sm text-zinc-400 mb-4">Per utilizzare l&apos;app devi attivare la geolocalizzazione e consentire l&apos;accesso alla tua posizione.</p>
          <div className="bg-zinc-800 rounded-sm p-4 text-left mb-4">
            <p className="text-xs font-bold uppercase text-[#FF5200] mb-2">Come attivare:</p>
            <ol className="text-xs text-zinc-400 space-y-1.5 list-decimal list-inside">
              <li>Tocca l&apos;<b className="text-zinc-300">icona lucchetto</b> nella barra indirizzi</li>
              <li>Tocca <b className="text-zinc-300">Autorizzazioni</b> o <b className="text-zinc-300">Impostazioni sito</b></li>
              <li>Cambia <b className="text-zinc-300">Posizione</b> in &quot;Consenti&quot;</li>
              <li>Ricarica la pagina</li>
            </ol>
          </div>
          <button onClick={() => window.location.reload()} className="w-full h-11 bg-[#FF5200] text-white text-sm font-bold uppercase rounded-sm" data-testid="gps-reload-btn">
            Ho attivato il GPS, ricarica
          </button>
        </div>
      </div>
    );
  }

  // Build ctx bag shared across screens
  const ctx = {
    driverInfo, work, active, completed, sortedActive, nextJob, nextDist, nextEta,
    myLocation, orderVersion, allItems, selectedJob, multiPodJobs,
    shiftStatus, shiftStartTime, breakCount,
    multiSelectMode, multiSelectedIds, showMultiPodWarning, pendingMultiPodJobs,
    showScanner, setShowScanner, showPushHelp, setShowPushHelp,
    confirmEvent, setConfirmEvent, showEndShiftSlide, setShowEndShiftSlide,
    pushEnabled, installPrompt, setInstallPrompt,
    listTab, setListTab, saving,
    podPhoto, setPodPhoto, podSignature, setPodSignature,
    podSignedBy, setPodSignedBy, podNote, setPodNote,
    showSignature, setShowSignature,
    showAnomaly, setShowAnomaly, anomalyType, setAnomalyType,
    anomalyNote, setAnomalyNote, anomalyPhoto, setAnomalyPhoto,
    preferredCamera,
    etaBadge, handleScan, navigateTo, togglePush, logout, goBack,
    requestTimesheetEvent, recordTimesheetEvent,
    setMultiSelectMode, setMultiSelectedIds, toggleMultiSelect, exitMultiSelect,
    startMultiPod, proceedWithMultiPod, setShowMultiPodWarning, setPendingMultiPodJobs,
    setMultiPodJobs, updateStatus, submitPod, submitAnomaly,
    capturePhoto, toggleCamera, handleReorder,
  };

  // Router
  if (screen === "home") return <HomeScreen {...ctx} />;
  if (screen === "list") return <ListScreen {...ctx} />;
  if (screen === "detail" && selectedJob) return <DetailScreen {...ctx} />;
  if (screen === "pod" && !selectedJob && multiPodJobs.length === 0) {
    // Safety fallback: land here without any job → go home
    setScreen("home");
    return null;
  }
  if (screen === "pod" && (selectedJob || multiPodJobs.length > 0)) return <PodScreen {...ctx} />;
  if (screen === "absences") {
    return (
      <div className="min-h-screen bg-[#09090B] text-white" data-testid="driver-absences-screen">
        <DriverHeader showBack title="Le Mie Assenze" driverInfo={driverInfo} goBack={goBack} navigateTo={navigateTo} togglePush={togglePush} pushEnabled={pushEnabled} setShowScanner={setShowScanner} installPrompt={installPrompt} setInstallPrompt={setInstallPrompt} logout={logout} />
        <div className="p-4">
          <AbsenceRequestSection token={token} />
        </div>
      </div>
    );
  }

  return null;
}
